import { Badge } from '@auctisync/ui';
import { FEATURES, TIER_LABEL, isPreviewingFeature, type FeatureKey } from '@auctisync/shared';
import { isKnownTier, planRowState } from './planPanel.logic';

/**
 * "Your plan" — the first surface in the product to actually READ the tier
 * entitlement ladder.
 *
 * Until now `hasFeature` had no callers anywhere. The ladder was correct and
 * enforced nothing, which is how `complimentary` (fixed 2026-08-13) and `trial`
 * (fixed 2026-08-25) could each sit entitled-to-nothing for weeks without a
 * single visible symptom — a denied feature and a feature nobody had wired look
 * identical from the outside.
 *
 * Jim, 2026-08-25: "For the demo, why not show all available features? We would
 * make a note that it is Professional tier option." This is that note. A trial
 * sees every row as included AND is told which plan actually carries it, so the
 * person evaluating the product is never quietly shown something they would
 * have to pay more for without knowing.
 *
 * ⚠️ THIS IS A READOUT, NOT A GATE. Nothing here hides or disables anything;
 * it reports what the ladder says. Enforcement is deliberately still absent —
 * see the no-ghost-features rule in CLAUDE.md: a Phase 1 tenant must not be
 * shown controls for features that do not exist yet, and several rows below
 * (Shopify, simulcast, warehouse export) are not built. Naming a plan is
 * honest; offering a button that does nothing is not.
 */

/** Display order — cheapest gate first, so the ladder reads top to bottom. */
const ROWS: { key: FeatureKey; label: string; blurb: string }[] = [
  {
    key: FEATURES.CONSIGNOR_STATEMENTS,
    label: 'Consignor statements',
    blurb: 'Settlement statements for the people who consign lots to you.',
  },
  {
    key: FEATURES.CUSTOM_DOMAIN,
    label: 'Custom domain',
    blurb: 'Run your sales on your own web address instead of ours.',
  },
  {
    key: FEATURES.MARKETPLACE_LISTING,
    label: 'Marketplace listing',
    blurb: 'Your lots also appear on our buyer-facing marketplaces.',
  },
  {
    key: FEATURES.WHITE_LABEL_REMOVAL,
    label: 'Remove AuctiSync branding',
    blurb: 'Your bidders see your house, not ours.',
  },
  {
    key: FEATURES.SHOPIFY_INTEGRATION,
    label: 'Shopify integration',
    blurb: 'Sync unsold stock through to a Shopify storefront.',
  },
  {
    key: FEATURES.AI_CATALOGING,
    label: 'AI cataloguing',
    blurb: 'Draft lot titles and descriptions from photographs.',
  },
  {
    key: FEATURES.BRANDED_APPS,
    label: 'Your own branded apps',
    blurb: 'Your house in the App Store and Play Store under your own name.',
  },
  {
    key: FEATURES.LIVE_SIMULCAST,
    label: 'Live simulcast',
    blurb: 'Run a live floor sale and online bidding together.',
  },
  {
    key: FEATURES.MULTI_LOCATION,
    label: 'Multiple locations',
    blurb: 'Separate salerooms, stock and pickup addresses.',
  },
  {
    key: FEATURES.API_ACCESS,
    label: 'API access',
    blurb: 'Drive AuctiSync from your own systems.',
  },
  {
    key: FEATURES.DATA_WAREHOUSE_EXPORT,
    label: 'Data warehouse export',
    blurb: 'Scheduled bulk export of your sales data.',
  },
  {
    key: FEATURES.DEDICATED_CSM,
    label: 'Dedicated account manager',
    blurb: 'A named person who knows your account.',
  },
];

export function PlanPanel({ tier }: { tier: string | null | undefined }) {
  // An unknown tier is a real condition, not an edge case to paper over: it is
  // exactly the shape of the bug this panel exists downstream of. Say so rather
  // than rendering a table of confident falses.
  if (!isKnownTier(tier)) {
    return (
      <section style={sectionStyle}>
        <h2 style={headingStyle}>Your plan</h2>
        <p style={{ color: 'var(--text-muted)', margin: '0.35rem 0 0' }}>
          {tier
            ? `This house is on an unrecognised plan (“${tier}”), so entitlements cannot be shown. ` +
              `That usually means the plan was added to the database without being added to the ` +
              `entitlement ladder — worth fixing before it silently denies features.`
            : 'No plan is recorded for this house yet.'}
        </p>
      </section>
    );
  }

  const t = tier;
  const planName = TIER_LABEL[t];
  const previewing = ROWS.some((r) => isPreviewingFeature(t, r.key));

  return (
    <section style={sectionStyle}>
      <header style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', flexWrap: 'wrap' }}>
        <h2 style={headingStyle}>Your plan</h2>
        <Badge variant="secondary">{planName}</Badge>
      </header>

      {previewing && (
        <p style={{ color: 'var(--text-muted)', margin: '0.5rem 0 0', fontSize: '0.9rem' }}>
          You're on a trial, so everything below is switched on. Each row names the plan that
          includes it, so you can see what you'd be choosing when the trial ends.
        </p>
      )}

      <ul style={{ listStyle: 'none', padding: 0, margin: '1rem 0 0' }}>
        {ROWS.map((row) => {
          const { included, carriedBy, previewBadge, upgradeBadge } = planRowState(t, row.key);

          return (
            <li
              key={row.key}
              style={{
                display: 'flex',
                gap: '0.75rem',
                alignItems: 'flex-start',
                padding: '0.6rem 0',
                borderTop: '1px solid var(--admin-hairline, var(--border))',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  color: included ? 'var(--text-success, #15803d)' : 'var(--text-muted)',
                  fontWeight: 700,
                  lineHeight: 1.4,
                  minWidth: '1rem',
                }}
              >
                {included ? '✓' : '—'}
              </span>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}
                >
                  <span style={{ color: 'var(--text)', fontWeight: 600 }}>{row.label}</span>

                  {/* The note Jim asked for. On a trial it captions an included
                      row ("Included with Professional"); on a paid plan it only
                      appears where the feature is NOT included, where it reads
                      as what you would upgrade to. */}
                  {previewBadge && <Badge variant="outline">{previewBadge}</Badge>}
                  {upgradeBadge && <Badge variant="outline">{upgradeBadge}</Badge>}
                </div>

                <p
                  style={{
                    margin: '0.15rem 0 0',
                    fontSize: '0.85rem',
                    color: 'var(--text-muted)',
                  }}
                >
                  {row.blurb}
                </p>
              </div>

              <span className="sr-only">
                {included ? 'Included in your plan' : `Not included — needs ${carriedBy}`}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

const sectionStyle: React.CSSProperties = {
  padding: '1rem 1.25rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-card, 0.5rem)',
  marginBottom: '1.25rem',
  // Anchor colour and surface at the ROOT rather than relying on every
  // descendant to set its own. Without these the panel inherits whatever
  // encloses it — on a dark surface that meant list rows inheriting white,
  // readable only by the accident that each visible label happened to set a
  // colour of its own. Anything added later without one would vanish.
  background: 'var(--surface-elev, var(--surface, transparent))',
  color: 'var(--text)',
};

const headingStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '1.05rem',
  color: 'var(--text)',
};
